/**
 * Test Harness for SystemBurdenResults Component
 *
 * This allows us to test the component with mock data without needing
 * a database connection or valid diagnostic code.
 *
 * Access at: /test-system-burden-results
 */

import React, { useState } from 'react';
import { PERSONA_BASELINES } from '../utils/algorithms';

// Import unified CSS
import '../styles/diagnostics/base.css';
import '../styles/diagnostics/components/pies.css';
import '../styles/diagnostics/components/legends.css';
import '../styles/diagnostics/components/interventions.css';
import '../styles/diagnostics/components/dependencies.css';
import '../styles/diagnostics/components/recommendations.css';
import '../styles/diagnostics/components/highlight-box.css';

function SystemBurdenResultsTest() {
  // Test with different zones
  const [zone, setZone] = useState('foundational'); // foundational, stretch, or mastery
  const [dcf, setDcf] = useState(0.50); // 0.50 = foundational, 0.65 = stretch, 0.80 = mastery

  // Load zone CSS dynamically
  React.useEffect(() => {
    if (zone) {
      import(`../styles/diagnostics/zones/${zone}.css`);
    }
  }, [zone]);

  // Mock data
  const mockResults = {
    personaId: 'mark',
    valueAtStake: 15.0,
    leaderName: 'Jane Smith (TEST)',
    scores: {
      dep1: 3, dep2: 4, dep3: 3, dep4: 6, dep5: 5, dep6: 4, dep7: 7,
      dep8: 5, dep9: 4, dep10: 3, dep11: 6, dep12: 7, dep13: 8, dep14: 6
    },
    calculations: {
      dcf: dcf,
      lpff: 0.55,
      totalLoss: 6.45,
      systemLoss: 4.42,
      leadershipLoss: 2.03,
      crf: 0.45,
      recoverableValue: 2.90,
      finalCaptureRate: 0.62,
      isValid: true
    }
  };

  const persona = PERSONA_BASELINES[mockResults.personaId];

  // Calculate values first
  const totalValue = mockResults.valueAtStake;
  const stretchValueAvailable = totalValue * 0.067; // 6.7% stretch potential

  // Get zone content
  const getZoneContent = () => {
    if (zone === 'foundational') {
      return {
        highlightLabel: 'Below Baseline Threshold',
        highlightValue: 'Foundational',
        highlightSubtext: 'Building essential organizational capability to access average transformation value (57%)',
        challengerQuestion1: `"Your system capability scores place you below the baseline threshold. This means you're not yet positioned to access the 57% average value that most transformations achieve. If we could develop your transformation foundations to that capability, what would this mean for your £${totalValue}M transformation?"`,
        challengerQuestion2: '"The dark green shows what baseline capability looks like—the 57% average value most transformations achieve. The light blue? That\'s stretch potential once you\'ve built the foundation. First step: build foundational system capability to reach baseline. Are you ready?"',
        keyInsight: 'Building foundational system capability unlocks access to transformation value. Your scores indicate you\'re below the baseline threshold needed to access the 57% average value. Once you reach baseline, stretch interventions become effective and accessible value increases significantly.'
      };
    } else if (zone === 'stretch') {
      return {
        highlightLabel: 'Remaining Potential Value',
        highlightValue: `£${stretchValueAvailable.toFixed(2)}M`,
        highlightSubtext: 'With your solid foundation you can stretch further, are you ready?',
        challengerQuestion1: `"If your transformation is worth £${totalValue}M, how much of that will you actually get?"`,
        challengerQuestion2: `"The dark green slice? That's your foundational value. The light green slice? That's your enhanced value gain by working towards excellence. The last £${stretchValueAvailable.toFixed(2)}M is your stretch. Do you want it?"`,
        keyInsight: 'Your baseline capability is strong. Stretch interventions can now unlock additional value beyond the average transformation. Focus on optimizing the highest-impact dependencies to maximize your capture rate within the empirical ceiling.'
      };
    } else {
      return {
        highlightLabel: 'Remaining Potential Value',
        highlightValue: `£${(totalValue * 0.018).toFixed(2)}M`,
        highlightSubtext: 'Although you\'re currently in the top 10% you could stretch further, are you ready?',
        challengerQuestion1: `"If your transformation is worth £${totalValue}M, you're already capturing £${(totalValue * 0.652).toFixed(2)}M — 8 points above average. The question is: can you fine-tune to reach the ceiling?"`,
        challengerQuestion2: '"The dark green? That\'s your foundational value. The light green? That\'s your enhanced value gain as a top performer. Your stretch is the final 1.8 points to reach peak performance. Excellence awaits you."',
        keyInsight: 'You\'re approaching the empirical ceiling for transformation value capture. The priority shifts to maintaining this exceptional capability under pressure. Focus on resilience, sustainability, and preventing regression in your top dependencies.'
      };
    }
  };

  // Get top 3 dependencies
  const getTop3Dependencies = () => {
    const dependencies = [
      { id: 1, name: 'Strategic Clarity and Value Case Quality', description: 'Clear transformation goals, compelling business case, measurable outcomes' },
      { id: 2, name: 'Leadership Buy-in and Active Sponsorship', description: 'Visible executive support, consistent messaging, resource commitment' },
      { id: 3, name: 'Clear Success Metrics and Progress Visibility', description: 'Measurable outcomes, regular reporting, transparent tracking' },
      { id: 4, name: 'Effective Cross-Functional Collaboration', description: 'Team coordination, information sharing, collaborative culture' },
      { id: 5, name: 'Change Capacity and Resource Availability', description: 'Sufficient capacity, available resources, manageable workload' },
      { id: 6, name: 'Stakeholder Engagement and Communication', description: 'Active engagement, clear communication, stakeholder alignment' },
      { id: 7, name: 'Technical Capability and Infrastructure', description: 'Technology readiness, infrastructure support, technical skills' },
      { id: 8, name: 'Risk Management and Contingency Planning', description: 'Risk identification, mitigation strategies, contingency plans' },
      { id: 9, name: 'Cultural Readiness for Change', description: 'Change-ready culture, psychological safety, growth mindset' },
      { id: 10, name: 'Decision-Making Speed and Clarity', description: 'Fast decisions, clear authority, empowered teams' },
      { id: 11, name: 'Knowledge Transfer and Capability Building', description: 'Training programs, knowledge sharing, skill development' },
      { id: 12, name: 'External Partner and Vendor Alignment', description: 'Partner coordination, vendor management, aligned objectives' },
      { id: 13, name: 'Regulatory and Compliance Readiness', description: 'Compliance awareness, regulatory understanding, legal alignment' },
      { id: 14, name: 'Financial Planning and Budget Flexibility', description: 'Budget availability, financial agility, investment capacity' }
    ];

    const scoredDependencies = dependencies.map((dep, index) => ({
      ...dep,
      score: mockResults.scores[`dep${index + 1}`] || 5
    }));

    return scoredDependencies.sort((a, b) => a.score - b.score).slice(0, 3);
  };

  // Get score band class
  const getScoreBand = (score) => {
    if (score <= 2) return 'critical';
    if (score <= 4) return 'weak';
    if (score <= 6) return 'moderate';
    if (score <= 8) return 'strong';
    return 'excellent';
  };

  // Get impact text based on score and dependency name
  const getImpactText = (score, name) => {
    if (score <= 2) {
      if (name === 'Strategic Clarity and Value Case Quality') {
        return 'Critical constraint blocking progress. Without strategic clarity, your transformation efforts disperse rather than compound. This dependency needs immediate attention as it\'s a foundational capability that enables all other work.';
      } else if (name === 'Hidden Work and Dependence Load') {
        return 'Critical constraint. High coordination overhead and key person dependencies are consuming transformation capacity. Making work visible and reducing dependence load will directly increase your ability to build baseline capability.';
      }
      return `Critical constraint blocking progress. Without ${name.toLowerCase()}, your transformation efforts disperse rather than compound. This dependency needs immediate attention as it's a foundational capability that enables all other work.`;
    } else if (score <= 4) {
      if (name === 'Delivery Flow and Interdependency Management') {
        return 'Structural weakness with frequent issues. Poor flow management means initiatives block each other and delay compounding benefits. Addressing this early will materially lift your recoverability factor.';
      }
      return `Structural weakness with frequent issues. Poor ${name.toLowerCase()} means initiatives block each other and delay compounding benefits. Addressing this early will materially lift your recoverability factor.`;
    } else {
      return `Working but inconsistent. This dependency is functional but fragile under pressure. Strengthening it will improve transformation resilience and value capture.`;
    }
  };

  const top3Deps = getTop3Dependencies();

  const zoneContent = getZoneContent();
  const foundationalValue = totalValue * 0.57;
  const potentialStretch = totalValue * 0.10;
  const permanentlyLost = totalValue * 0.33;
  const totalPotential = foundationalValue + potentialStretch;
  const systemValue = totalPotential * 0.685;
  const leadershipValue = totalPotential * 0.315;

  // Stretch zone specific calculations (4 segments)
  const stretchValueGained = totalValue * 0.033; // 3.3% extra value gained
  // stretchValueAvailable already calculated above

  // Mastery zone specific calculations (4 segments)
  const masteryValueGained = totalValue * 0.082; // 8.2% extra value gained
  const masteryValueAvailable = totalValue * 0.018; // 1.8% stretch potential remaining

  // Handle zone change
  const handleZoneChange = (newZone) => {
    setZone(newZone);
    if (newZone === 'foundational') setDcf(0.50);
    if (newZone === 'stretch') setDcf(0.65);
    if (newZone === 'mastery') setDcf(0.80);
  };

  return (
    <div className={`zone-${zone}`}>
      {/* Test Controls */}
      <div style={{
        position: 'fixed',
        top: '10px',
        right: '10px',
        background: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        zIndex: 1000
      }}>
        <h4 style={{ marginTop: 0, marginBottom: '12px', fontSize: '14px', fontWeight: 'bold' }}>Test Controls</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            onClick={() => handleZoneChange('foundational')}
            style={{
              padding: '8px 16px',
              background: zone === 'foundational' ? '#16a34a' : '#e5e7eb',
              color: zone === 'foundational' ? 'white' : '#374151',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            Foundational (DCF &lt; 0.6)
          </button>
          <button
            onClick={() => handleZoneChange('stretch')}
            style={{
              padding: '8px 16px',
              background: zone === 'stretch' ? '#f59e0b' : '#e5e7eb',
              color: zone === 'stretch' ? 'white' : '#374151',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            Stretch (0.6-0.75)
          </button>
          <button
            onClick={() => handleZoneChange('mastery')}
            style={{
              padding: '8px 16px',
              background: zone === 'mastery' ? '#0EA5E9' : '#e5e7eb',
              color: zone === 'mastery' ? 'white' : '#374151',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            Mastery (≥ 0.75)
          </button>
        </div>
        <p style={{ fontSize: '12px', marginTop: '12px', marginBottom: 0, color: '#6b7280' }}>
          Current DCF: {dcf.toFixed(2)}
        </p>
      </div>

      {/* Actual Component Content */}
      <div className="container">
        {/* Header */}
        <div className="card">
          <div className="badge">STEP 4 OF 4 - TEST MODE</div>
          <h1>Your System Burden Assessment Results</h1>
          <p style={{ color: '#697A90', marginBottom: '16px' }}>
            Organizational system capability analysis and transformation value recovery potential, for <strong>{mockResults.leaderName}</strong>
          </p>
          <div className="persona-info">
            <p><strong>Selected Profile:</strong> {persona.role}</p>
            <p><strong>Leadership Strain:</strong> {persona.strain}</p>
          </div>
        </div>

        {/* Value Analysis */}
        <div className="card">
          <h2>Value Analysis</h2>

          <div className="challenger-question">
            {zoneContent.challengerQuestion1}
          </div>

          <p style={{ marginBottom: '8px' }}>
            McKinsey studied thousands of transformations. Here's what's happening to your business case:
          </p>

          <div className="pie-section">
            <div className="pie-container">
              <div className="pie-chart pie-chart-full"></div>
              <div className="pie-center">
                <div className="pie-center-label">Total Value</div>
                <div className="pie-center-value">£{totalValue}M</div>
              </div>
            </div>

            <div className="legend">
              {zone === 'stretch' ? (
                <>
                  <div className="legend-item">
                    <div className="legend-color color-captured"></div>
                    <div className="legend-content">
                      <div className="legend-title">Foundational value realised</div>
                      <div className="legend-description">Your foundational value gains — the baseline</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{foundationalValue.toFixed(2)}M</div>
                      <div className="legend-percent">57%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-recoverable"></div>
                    <div className="legend-content">
                      <div className="legend-title">Extra value you're gaining</div>
                      <div className="legend-description">Your enhanced value gains — the dividends of building on solid foundational practices</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{stretchValueGained.toFixed(2)}M</div>
                      <div className="legend-percent">3.3%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-potential"></div>
                    <div className="legend-content">
                      <div className="legend-title">Your stretch</div>
                      <div className="legend-description">Further value gains available to you</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{stretchValueAvailable.toFixed(2)}M</div>
                      <div className="legend-percent">6.7%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-lost"></div>
                    <div className="legend-content">
                      <div className="legend-title">Permanently lost</div>
                      <div className="legend-description">Transformation friction — can't be eliminated</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{permanentlyLost.toFixed(2)}M</div>
                      <div className="legend-percent">33%</div>
                    </div>
                  </div>
                </>
              ) : zone === 'mastery' ? (
                <>
                  <div className="legend-item">
                    <div className="legend-color color-captured"></div>
                    <div className="legend-content">
                      <div className="legend-title">Foundational value realised</div>
                      <div className="legend-description">Your foundational value gains — the baseline</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{foundationalValue.toFixed(2)}M</div>
                      <div className="legend-percent">57%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-recoverable"></div>
                    <div className="legend-content">
                      <div className="legend-title">Extra value you're gaining</div>
                      <div className="legend-description">Your enhanced value gains — the dividends of being in the top 10% of performers</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{masteryValueGained.toFixed(2)}M</div>
                      <div className="legend-percent">8.2%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-potential"></div>
                    <div className="legend-content">
                      <div className="legend-title">Your stretch</div>
                      <div className="legend-description">Further value gains available to you</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{masteryValueAvailable.toFixed(2)}M</div>
                      <div className="legend-percent">1.8%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-lost"></div>
                    <div className="legend-content">
                      <div className="legend-title">Permanently lost</div>
                      <div className="legend-description">Transformation friction — can't be eliminated</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{permanentlyLost.toFixed(2)}M</div>
                      <div className="legend-percent">33%</div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="legend-item">
                    <div className="legend-color color-captured"></div>
                    <div className="legend-content">
                      <div className="legend-title">Foundational Value</div>
                      <div className="legend-description">Baseline capability every transformation should achieve</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{foundationalValue.toFixed(2)}M</div>
                      <div className="legend-percent">57%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-potential"></div>
                    <div className="legend-content">
                      <div className="legend-title">Potential Further Stretch</div>
                      <div className="legend-description">Additional value accessible once baseline is secured</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{potentialStretch.toFixed(2)}M</div>
                      <div className="legend-percent">10%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-lost"></div>
                    <div className="legend-content">
                      <div className="legend-title">Permanently Lost</div>
                      <div className="legend-description">Transformation friction — can't be eliminated</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{permanentlyLost.toFixed(2)}M</div>
                      <div className="legend-percent">33%</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <p className="source">Source: McKinsey & Company, "Losing from day one", December 2021</p>
        </div>

        {/* Highlight: Zone */}
        <div className="highlight-box">
          <div className="highlight-label">{zoneContent.highlightLabel}</div>
          <div className="highlight-value">{zoneContent.highlightValue}</div>
          <div className="highlight-subtext">{zoneContent.highlightSubtext}</div>
        </div>

        {/* How Do You Capture It? */}
        <div className="card">
          <h2>How Do You Capture It?</h2>

          <div className="challenger-question">
            {zoneContent.challengerQuestion2}
          </div>

          <p style={{ marginBottom: '8px' }}>
            Value capture comes from two intervention channels. Here's the breakdown:
          </p>

          <div className="two-pie-section">
            {/* Left: The breakdown */}
            <div className="pie-panel">
              <div className="pie-panel-title">{zone === 'mastery' ? 'Optimization by Intervention Type' : 'Capture by Intervention Type'}</div>
              <div className="pie-panel-subtitle">Where does the £{zone === 'stretch' ? stretchValueAvailable.toFixed(2) : zone === 'mastery' ? masteryValueAvailable.toFixed(2) : totalPotential.toFixed(2)}M come from?</div>

              <div className="pie-small-container">
                <div className="pie-small pie-chart-intervention">
                  <div className="pie-small-center">
                    <div className="pie-small-label">{zone === 'mastery' ? 'Remaining' : 'Potential'}</div>
                    <div className="pie-small-value">£{zone === 'stretch' ? stretchValueAvailable.toFixed(2) : zone === 'mastery' ? masteryValueAvailable.toFixed(2) : totalPotential.toFixed(2)}M</div>
                  </div>
                </div>
              </div>

              <div className="legend-compact">
                <div className="legend-compact-item">
                  <div className="legend-compact-color color-system"></div>
                  <div className="legend-compact-content">
                    <div className="legend-compact-title">System Interventions</div>
                    <div className="legend-compact-desc">Organisational capability improvements</div>
                  </div>
                  <div className="legend-compact-value">
                    <div className="legend-compact-amount">£{zone === 'stretch' ? (stretchValueAvailable * 0.685).toFixed(2) : zone === 'mastery' ? (masteryValueAvailable * 0.685).toFixed(2) : systemValue.toFixed(2)}M</div>
                    <div className="legend-compact-percent">68.5%</div>
                  </div>
                </div>
                <div className="legend-compact-item">
                  <div className="legend-compact-color color-leadership"></div>
                  <div className="legend-compact-content">
                    <div className="legend-compact-title">Leadership Interventions</div>
                    <div className="legend-compact-desc">Psychological flexibility coaching</div>
                  </div>
                  <div className="legend-compact-value">
                    <div className="legend-compact-amount">£{zone === 'stretch' ? (stretchValueAvailable * 0.315).toFixed(2) : zone === 'mastery' ? (masteryValueAvailable * 0.315).toFixed(2) : leadershipValue.toFixed(2)}M</div>
                    <div className="legend-compact-percent">31.5%</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: What this means */}
            <div className="pie-panel">
              <div className="pie-panel-title">What This Means</div>
              <div className="pie-panel-subtitle">Two levers, one goal: {zone === 'stretch' ? 'capture more value' : zone === 'mastery' ? 'optimization' : 'build baseline capability'}</div>

              <div style={{ padding: '16px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ width: '40px', height: '40px', background: '#0EA5E9', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '700' }}>68%</div>
                  <div>
                    <div style={{ fontWeight: '600', color: '#152C47' }}>System capability</div>
                    <div style={{ fontSize: '13px', color: '#697A90' }}>Fix the machine: dependencies, governance, delivery flow</div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ width: '40px', height: '40px', background: '#8B5CF6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '700' }}>32%</div>
                  <div>
                    <div style={{ fontWeight: '600', color: '#152C47' }}>Leadership flexibility</div>
                    <div style={{ fontSize: '13px', color: '#697A90' }}>Free the leadership: reduce psychological burden</div>
                  </div>
                </div>

                <div style={{ background: zone === 'stretch' ? '#fef3c7' : zone === 'mastery' ? '#d1fae5' : '#fee2e2', borderRadius: '8px', padding: '16px', marginTop: '16px' }}>
                  <div style={{ fontSize: '13px', color: zone === 'stretch' ? '#92400e' : zone === 'mastery' ? '#065f46' : '#991b1b' }}>
                    <strong>Key insight:</strong> {zone === 'stretch' ? 'Your performance is based on a solid foundation. The remaining potential comes from a stretch towards excellence by adding and embedding enhanced practices to compound this success.' : zone === 'mastery' ? 'Your performance is already well on the way to mastery. The remaining potential comes from fine-tuning and embedding the practices to further compound the success, rather than recovery.' : zoneContent.keyInsight}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Intervention Cards */}
          <div className="intervention-cards">
            <div className="intervention-card intervention-system">
              <div className="intervention-label">System Interventions</div>
              <div className="intervention-title">
                {zone === 'mastery' ? 'UB4 + DF System Optimization' : 'UB4 + DF System Intervention'}
              </div>
              <div className="intervention-value">£{zone === 'stretch' ? (stretchValueAvailable * 0.685).toFixed(2) : zone === 'mastery' ? (masteryValueAvailable * 0.685).toFixed(2) : systemValue.toFixed(2)}M</div>
              <div className="intervention-desc">
                {zone === 'mastery'
                  ? 'The targetted outcome for fine-tuning organizational capability through advanced Uncertainty Management practices'
                  : zone === 'stretch'
                  ? 'The targetted outcome for organisational capability improvement through the Uncertainty Management framework'
                  : 'Build foundational organizational capability through strengthening the 14 dependency factors to reach baseline threshold'
                }
              </div>
              <ul className="intervention-items">
                {zone === 'mastery' ? (
                  <>
                    <li>14 dependency factors already strong</li>
                    <li>Incremental improvements to embed excellence</li>
                    <li>Fine-tuning governance and delivery practices</li>
                  </>
                ) : zone === 'stretch' ? (
                  <>
                    <li>14 dependency factors assessed</li>
                    <li>Hidden work surfaced and addressed</li>
                    <li>Governance and delivery optimised</li>
                  </>
                ) : (
                  <>
                    <li>Strategic clarity and value case quality</li>
                    <li>Delivery flow and interdependency management</li>
                    <li>Hidden work visibility and management</li>
                    <li>Portfolio governance and decision rights</li>
                    <li>Change implementation foundations</li>
                    <li>Stakeholder network health development</li>
                  </>
                )}
              </ul>
            </div>

            <div className="intervention-card intervention-leadership">
              <div className="intervention-label">Leadership Interventions</div>
              <div className="intervention-title">
                {zone === 'mastery' ? 'UB4 + DF Leadership Optimization' : 'UB4 + DF Leadership Intervention'}
              </div>
              <div className="intervention-value">£{zone === 'stretch' ? (stretchValueAvailable * 0.315).toFixed(2) : zone === 'mastery' ? (masteryValueAvailable * 0.315).toFixed(2) : leadershipValue.toFixed(2)}M</div>
              <div className="intervention-desc">
                {zone === 'mastery'
                  ? 'The targetted outcome through advanced ACT-based executive coaching to sustain psychological flexibility at peak performance'
                  : 'The targetted outcome through ACT-based executive coaching to improve psychological flexibility'
                }
              </div>
              <ul className="intervention-items">
                {zone === 'mastery' ? (
                  <>
                    <li>10 anxiety patterns already well-managed</li>
                    <li>Advanced optimization coaching</li>
                    <li>Sustaining peak psychological flexibility</li>
                  </>
                ) : (
                  <>
                    <li>10 anxiety patterns addressed</li>
                    <li>Evidence-based (Skews 2018 RCT)</li>
                    <li>8% effect size on mental wellbeing</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Top 3 Dependencies */}
        <div className="card">
          <h2>Top 3 System Capability Dependencies Requiring Attention</h2>
          <p style={{ color: '#697A90', marginBottom: '16px' }}>
            These dependencies have the lowest capability scores and highest impact on transformation success.
          </p>

          <div className="dependency-list">
            {top3Deps.map((dep, index) => (
              <div key={dep.id} className={`dependency-item ${getScoreBand(dep.score)}`}>
                <div className="dependency-header">
                  <div>
                    <div className="dependency-title">{index + 1}. {dep.name}</div>
                    <div className="dependency-description">{dep.description}</div>
                  </div>
                  <div className="dependency-score">{dep.score}/10</div>
                </div>
                <div className="dependency-impact">
                  <p><strong>Impact:</strong> {getImpactText(dep.score, dep.name)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recommendations */}
        <div className="card">
          <h2>Recommended Next Steps</h2>
          <p style={{ color: '#697A90', marginBottom: '16px' }}>
            <strong>{mockResults.leaderName}</strong>, based on your data, here are evidence-based interventions to {zone === 'foundational' ? 'build foundational system capability' : zone === 'stretch' ? 'capture your stretch potential' : 'sustain transformation mastery'}:
          </p>

          {zone === 'foundational' ? (
            <>
              <div className="recommendation-item">
                <h4>1. Establish Baseline Strategic Clarity</h4>
                <p>Run a strategic alignment workshop to resolve contested assumptions and build a shared understanding of transformation goals. Use value mapping techniques to make the business case tangible and measurable. This foundational work is essential before recovery interventions can gain traction.</p>
              </div>

              <div className="recommendation-item">
                <h4>2. Build Basic Delivery Flow Capability</h4>
                <p>Conduct dependency mapping across your transformation portfolio to identify bottlenecks and blocking relationships. Implement basic flow management practices to create visibility and reduce the most critical coordination conflicts.</p>
              </div>

              <div className="recommendation-item">
                <h4>3. Surface and Manage Hidden Work</h4>
                <p>Run a hidden work audit to make invisible coordination visible. Map key person dependencies and develop initial mitigation strategies. Focus on making work explicit and manageable as a foundation for future optimization.</p>
              </div>
            </>
          ) : (
            <>
              <div className="recommendation-item">
                <h4>1. Establish Baseline Strategic Clarity</h4>
                <p>Run a strategic alignment workshop to resolve contested assumptions and build a shared understanding of transformation goals. Use value mapping techniques to make the business case tangible and measurable. This foundational work is essential before recovery interventions can gain traction.</p>
              </div>

              <div className="recommendation-item">
                <h4>2. Build Basic Delivery Flow Capability</h4>
                <p>Conduct dependency mapping across your transformation portfolio to identify bottlenecks and blocking relationships. Implement basic flow management practices to create visibility and reduce the most critical coordination conflicts.</p>
              </div>

              <div className="recommendation-item">
                <h4>3. Surface and Manage Hidden Work</h4>
                <p>Run a hidden work audit to make invisible coordination visible. Map key person dependencies and develop initial mitigation strategies. Focus on making work explicit and manageable as a foundation for future optimization.</p>
              </div>
            </>
          )}

          <div className="recommendation-item">
            <h4>4. Consider Leadership Psychological Flexibility Assessment</h4>
            <p>System improvements address 68.5% of transformation value loss. The remaining 31.5% is attributable to leadership-level factors. Consider completing the Leadership Anxiety Assessment to understand your psychological flexibility and identify opportunities to address the leadership-addressable portion of value at risk.</p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="cta-section">
          <div className="cta-question">
            {zone === 'stretch' ? (
              <>
                £{stretchValueAvailable.toFixed(2)}M is sitting on the table, there for the taking.<br />
                Are you daring enough to stretch for it?
              </>
            ) : zone === 'mastery' ? (
              <>
                You've almost captured all of the available value.<br />
                Ready to reach for mastery?
              </>
            ) : (
              <>
                Ready to build your transformation foundations?<br />
                Let's discuss your path to baseline capability
              </>
            )}
          </div>
          <div className="cta-subtext">
            {zone === 'stretch'
              ? 'Explore stretch interventions to maximize your transformation success'
              : zone === 'mastery'
              ? 'Explore mastery practices to maximize your transformation success'
              : 'Book a Truth Session to explore foundational system capability development'
            }
          </div>
          <p style={{ marginTop: '24px', marginBottom: '20px', opacity: '0.9' }}>
            {zone === 'stretch'
              ? "Book a Truth Session to discuss your results and explore the recommended next steps in detail. We'll help you identify the stretch interventions that will have the greatest impact on your transformation success."
              : zone === 'mastery'
              ? "Book a Truth Session to discuss your results and explore the recommended next steps in detail. We'll help you identify the mastery practices that will have the greatest impact on your transformation success."
              : "Schedule a call to discuss your dependency patterns and create a plan to build the foundational organizational capability that unlocks transformation value. We'll help you understand what reaching baseline threshold means for your system capability and your transformation."
            }
          </p>
          <button onClick={() => window.location.href = 'https://daringfutures.co.uk/book-truth-session'} className="btn-truth-session">
            Book Truth Session
          </button>
        </div>

        {/* Actions */}
        <div className="card">
          <div className="button-group">
            <button className="btn-secondary" onClick={() => window.print()}>Print Results</button>
            <button className="btn-secondary" onClick={() => alert('Back to Assessment (test mode)')}>
              Back to Assessment
            </button>
            <button className="btn-primary" onClick={() => alert('Start Leadership Assessment (test mode)')}>
              Start Leadership Assessment
            </button>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px', marginTop: '32px' }}>
          <p>Daring Futures • System Burden Diagnostic • v7.0.0 • Step 4 of 4</p>
        </div>

        {/* Test Success Message */}
        <div style={{
          background: '#d1fae5',
          border: '2px solid #10b981',
          borderRadius: '8px',
          padding: '24px',
          marginTop: '32px'
        }}>
          <h3 style={{ marginTop: 0, color: '#065f46' }}>✅ Test Mode Active</h3>
          <p style={{ color: '#047857', marginBottom: 0 }}>
            If you can see this page with styled components and the zone switcher works,
            the component is rendering correctly! Check the browser console for any errors.
          </p>
        </div>
      </div>
    </div>
  );
}

export default SystemBurdenResultsTest;
